/**
 * `useEditorKeyboardShortcuts` — single `onKeyDownCapture` handler for
 * the editor that wires up:
 *
 *   ⌘⇧V / Ctrl⇧V  → mark next paste as plain-text (consumed by clipboard hook)
 *   ⌘J  / Ctrl J  → toggle the AI prompt menu (gated by `enableAI`)
 *   ⌘A  / Ctrl A  → inside a table cell: select the cell's contents
 *                   (second press escalates to the table)
 *   Backspace     → block cross-cell jump at table cell start (collapsed sel)
 *   Delete        → block cross-cell jump at table cell end (collapsed sel)
 *   Tab           → indent inside table cells / list items; bubble otherwise
 *   Shift+Tab     → outdent inside the same contexts
 *   Enter         → schedule a clearMarksAtCaret on next microtask so the
 *                   new line starts unformatted (Google Docs behavior)
 *
 * Why a "capture" phase listener
 *   Plate's own keydown handlers fire on the bubble phase. By capturing we
 *   intercept the shortcut BEFORE Plate's defaults run, so e.g. Tab doesn't
 *   fall through to the browser's focus-cycle and the cell-boundary
 *   backspace guard runs before Plate's default merge.
 *
 * Why this hook takes several params
 *   Each shortcut hands off to a different subsystem:
 *     - markNextPasteAsPlain → clipboard hook
 *     - aiMenuRef.toggle()   → imperative ref to the AIMenu component
 *     - clearMarksAtCaret    → mark-reset hook
 *   We deliberately keep this hook as a thin orchestration layer that
 *   doesn't own any of those subsystems itself — the shell wires them up.
 */
import { useCallback } from 'react';
import { indent as plateIndent, outdent as plateOutdent } from '@platejs/indent';
import type { AIMenuHandle } from '../ai/AIMenu';

export interface UseEditorKeyboardShortcutsConfig {
    editor: any;
    readOnly: boolean;
    enableAI: boolean;
    /** From `useClipboardHandlers`. Flags the next paste as plain-text on ⌘⇧V. */
    markNextPasteAsPlain: () => void;
    /** Imperative ref to the AIMenu — used to toggle on ⌘J. */
    aiMenuRef: React.RefObject<AIMenuHandle | null>;
    /** From `useMarkResetOnNavigation`. Scheduled (microtask) on Enter. */
    clearMarksAtCaret: () => void;
}

export interface UseEditorKeyboardShortcutsReturn {
    handleKeyDownCapture: (e: React.KeyboardEvent) => void;
}

export function useEditorKeyboardShortcuts({
    editor,
    readOnly,
    enableAI,
    markNextPasteAsPlain,
    aiMenuRef,
    clearMarksAtCaret,
}: UseEditorKeyboardShortcutsConfig): UseEditorKeyboardShortcutsReturn {
    const handleKeyDownCapture = useCallback(
        (e: React.KeyboardEvent) => {
            // ⌘⇧V (Mac) / Ctrl⇧V (Win/Linux): mark the next paste as
            // plain-text only. We can't act on the paste here — the paste
            // event hasn't fired yet — so we tell the clipboard hook to
            // flag the next paste, and `handlePaste` consumes it.
            if (!readOnly && (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'v') {
                markNextPasteAsPlain();
                // Don't preventDefault — let the browser fire the paste event.
                return;
            }

            // ⌘J / Ctrl J: toggle the AI menu. Gated by `enableAI` so the
            // shortcut is a no-op when AI is disabled by the visibility
            // flag — the AIToolbarButton in PlateToolbar/FloatingToolbar
            // is also hidden, so there's no surface to invoke it at all.
            if (enableAI && !readOnly && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
                e.preventDefault();
                e.stopPropagation();
                aiMenuRef.current?.toggle();
                return;
            }

            // ⌘A / Ctrl A inside a table cell: scope the select-all to the
            // CELL's contents instead of the whole document. Default
            // contentEditable behaviour selects the entire editor, which is
            // almost never what the user wants when they're working inside a
            // cell (they expect the same scoping as Word / Google Docs).
            // Press it twice in a row to escalate to the surrounding table,
            // then a third time would fall through to the browser's
            // document-wide select.
            if (!readOnly && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'a') {
                const ed: any = editor;
                const sel = ed?.selection;
                if (!sel) return;
                try {
                    const cellAbove = ed?.api?.above?.({
                        match: (n: any) =>
                            n?.type === 'td' || n?.type === 'th' || n?.type === 'table_cell' || n?.type === 'table_cell_header',
                    });
                    if (!cellAbove) return; // not in a cell — let default fire
                    const cellPath: number[] = cellAbove[1];
                    const cellStart = ed?.api?.start?.(cellPath);
                    const cellEnd = ed?.api?.end?.(cellPath);
                    if (!cellStart || !cellEnd) return;
                    // Check whether the cell's whole contents are already
                    // selected — if so, escalate to the surrounding table.
                    const alreadyCellSelected =
                        (sel.anchor.path as number[]).join() === (cellStart.path as number[]).join() &&
                        sel.anchor.offset === cellStart.offset &&
                        (sel.focus.path as number[]).join() === (cellEnd.path as number[]).join() &&
                        sel.focus.offset === cellEnd.offset;
                    if (alreadyCellSelected) {
                        const tableAbove = ed?.api?.above?.({
                            match: (n: any) => n?.type === 'table',
                        });
                        if (tableAbove) {
                            const tablePath: number[] = tableAbove[1];
                            const tableStart = ed?.api?.start?.(tablePath);
                            const tableEnd = ed?.api?.end?.(tablePath);
                            if (tableStart && tableEnd) {
                                e.preventDefault();
                                e.stopPropagation();
                                ed?.tf?.select?.({ anchor: tableStart, focus: tableEnd });
                                return;
                            }
                        }
                        return; // escalation failed — fall through to default
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    ed?.tf?.select?.({ anchor: cellStart, focus: cellEnd });
                    return;
                } catch {
                    /* api shape may vary; let default fire */
                }
            }

            // Block backspace/delete from jumping across cell boundaries on a
            // collapsed selection; in-cell deletion + multi-char/multi-cell
            // selections still delete normally.
            if (!readOnly && (e.key === 'Backspace' || e.key === 'Delete')) {
                const ed: any = editor;
                const sel = ed?.selection;
                if (sel?.anchor && sel?.focus) {
                    const collapsed =
                        sel.anchor.offset === sel.focus.offset &&
                        (sel.anchor.path as number[]).join() === (sel.focus.path as number[]).join();
                    if (collapsed) {
                        try {
                            const cellAbove = ed?.api?.above?.({
                                match: (n: any) =>
                                    n?.type === 'td' || n?.type === 'th' || n?.type === 'table_cell' || n?.type === 'table_cell_header',
                            });
                            if (cellAbove) {
                                const cellPath: number[] = cellAbove[1];
                                const boundary = e.key === 'Backspace' ? ed?.api?.start?.(cellPath) : ed?.api?.end?.(cellPath);
                                if (
                                    boundary &&
                                    sel.anchor.offset === boundary.offset &&
                                    (sel.anchor.path as number[]).join() === (boundary.path as number[]).join()
                                ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                }
                            }
                        } catch {
                            /* api.above / api.start shape may vary across versions */
                        }
                    }
                }
            }

            // Tab inside a table cell or list item indents in-place. Otherwise
            // bubble so the browser handles focus cycling normally (e.g.,
            // tabbing out of the editor to the next focusable element).
            if (e.key === 'Tab') {
                const ed: any = editor;
                const block = ed?.api?.block?.({ block: true });
                const blockNode = block?.[0];
                let insideCell = false;
                try {
                    const above = ed?.api?.above?.({
                        match: (n: any) =>
                            n?.type === 'td' || n?.type === 'th' || n?.type === 'table_cell' || n?.type === 'table_cell_header',
                    });
                    insideCell = Boolean(above);
                } catch {
                    /* api.above shape may vary */
                }
                const inList = Boolean(blockNode?.listStyleType);
                if (!insideCell && !inList) return;
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    (plateOutdent as any)(ed);
                } else {
                    (plateIndent as any)(ed);
                }
                return;
            }

            // Enter (without Shift) → clear inline marks on the next microtask.
            // Deferred so Plate's native Enter handling runs first and creates
            // the new block; we only strip the active marks afterwards. With
            // Shift+Enter (soft break), marks stay alive — that's the
            // "continue typing in the same style on the same paragraph"
            // intent users expect.
            if (e.key === 'Enter' && !e.shiftKey) {
                Promise.resolve().then(clearMarksAtCaret);
            }
        },
        [editor, clearMarksAtCaret, readOnly, enableAI, markNextPasteAsPlain, aiMenuRef],
    );

    return { handleKeyDownCapture };
}
